import type { UpdateCoupleInput } from '@just-us/shared'
/**
 * pages/settings.tsx
 *
 * Settings screen — accessible via gear icon in the home header.
 * No bottom nav on this page.
 *
 * Sections:
 *   1. Couple settings (name + anniversary — creator-only edit)
 *   2. Distance mode toggle
 *   3. Notifications — enable push button
 *   4. Invite code (read-only, copyable)
 *   5. Sign out
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCouple, useUpdateCouple } from '../hooks/use-couple'
import { usePush } from '../hooks/use-push'
import { clearToken, getAuthPayload } from '../store/auth'
import { APP_VERSION } from '../store/version'

// ---------------------------------------------------------------------------
// Section wrapper
// ---------------------------------------------------------------------------

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section className="bg-white rounded-[16px] border border-[#F4C0D1] shadow-[0_2px_12px_rgba(212,83,126,0.08)] overflow-hidden">
    <div className="px-4 py-3 border-b border-[#F4C0D1]">
      <h2 className="font-['DM_Sans'] text-[13px] font-medium text-[#B89AA2] uppercase tracking-[0.06em]">
        {title}
      </h2>
    </div>
    <div className="divide-y divide-[#F9E0E8]">{children}</div>
  </section>
)

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

const Row = ({ children }: { children: React.ReactNode }) => (
  <div className="px-4 py-3">{children}</div>
)

// ---------------------------------------------------------------------------
// Label + input pair
// ---------------------------------------------------------------------------

const FieldRow = ({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  readOnly,
}: {
  label: string
  value: string
  onChange?: (v: string) => void
  type?: string
  placeholder?: string
  readOnly?: boolean
}) => (
  <Row>
    <label className="flex flex-col gap-1">
      <span className="font-['DM_Sans'] text-[12px] text-[#B89AA2]">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={placeholder}
        readOnly={readOnly}
        className={`h-[40px] rounded-[10px] border px-3 font-['DM_Sans'] text-[14px] text-[#2C1810] bg-[#FAF8F4] outline-none transition-all duration-150 ${
          readOnly
            ? 'border-[#F4C0D1] text-[#B89AA2] cursor-default select-all'
            : 'border-[#F4C0D1] focus:border-[#D4537E] focus:ring-2 focus:ring-[rgba(212,83,126,0.15)]'
        }`}
      />
    </label>
  </Row>
)

// ---------------------------------------------------------------------------
// Toggle row
// ---------------------------------------------------------------------------

const ToggleRow = ({
  label,
  description,
  checked,
  onChange,
}: {
  label: string
  description?: string
  checked: boolean
  onChange: (v: boolean) => void
}) => (
  <Row>
    <div className="flex items-center justify-between gap-3">
      <div>
        <p className="font-['DM_Sans'] text-[14px] text-[#2C1810]">{label}</p>
        {description && (
          <p className="font-['DM_Sans'] text-[12px] text-[#B89AA2] mt-0.5">{description}</p>
        )}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative w-[44px] h-[26px] rounded-full transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-[#D4537E] focus-visible:ring-offset-1 ${
          checked ? 'bg-[#D4537E]' : 'bg-[#E8D0D8]'
        }`}
      >
        <span
          className={`absolute top-[3px] w-[20px] h-[20px] rounded-full bg-white shadow-[0_1px_4px_rgba(0,0,0,0.18)] transition-transform duration-200 ${
            checked ? 'translate-x-[21px]' : 'translate-x-[3px]'
          }`}
        />
      </button>
    </div>
  </Row>
)

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function SettingsPage() {
  const navigate = useNavigate()
  const payload = getAuthPayload()
  const { data, isLoading } = useCouple()
  const updateCouple = useUpdateCouple()
  const {
    state: pushState,
    subscribeToPush,
    sendTestNotification,
    testState,
  } = usePush(payload?.user_id)
  const couple = data?.data?.couple
  const isCreator = couple?.creator_id === payload?.user_id

  // Local state for editable fields
  const [coupleName, setCoupleName] = useState('')
  const [anniversaryDate, setAnniversaryDate] = useState('')
  const [distanceMode, setDistanceMode] = useState(false)
  const [saved, setSaved] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  // Sync from API data once loaded
  const [initialised, setInitialised] = useState(false)
  if (couple && !initialised) {
    setCoupleName(couple.couple_name ?? '')
    setAnniversaryDate(couple.anniversary_date ?? '')
    setDistanceMode(couple.distance_mode)
    setInitialised(true)
  }

  const handleSave = async () => {
    if (!isCreator) return
    setSaveError(null)
    const input: UpdateCoupleInput = {}
    if (coupleName.trim()) input.couple_name = coupleName.trim()
    if (anniversaryDate) input.anniversary_date = anniversaryDate
    input.distance_mode = distanceMode

    try {
      await updateCouple.mutateAsync(input)
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } catch (e) {
      setSaveError((e as Error).message)
    }
  }

  const handleDistanceToggle = async (val: boolean) => {
    setDistanceMode(val)
    if (!isCreator) return
    try {
      await updateCouple.mutateAsync({ distance_mode: val })
    } catch {
      setDistanceMode(!val) // revert on failure
    }
  }

  const handleCopyInvite = async () => {
    if (!couple?.invite_code) return
    try {
      await navigator.clipboard.writeText(couple.invite_code)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // Clipboard not available — noop
    }
  }

  const handleSignOut = () => {
    clearToken()
    navigate('/', { replace: true })
  }

  return (
    <div className="min-h-screen bg-[#FAF8F4] flex flex-col max-w-[430px] mx-auto relative">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#FAF8F4] pt-[env(safe-area-inset-top)]">
        <div className="flex items-center justify-between px-5 h-[56px]">
          <button
            type="button"
            onClick={() => navigate('/home')}
            className="w-[36px] h-[36px] rounded-full bg-white border border-[#F4C0D1] flex items-center justify-center active:scale-90 transition-all duration-150 touch-manipulation"
            aria-label="Back"
          >
            <span className="text-[16px]">←</span>
          </button>
          <span className="font-['Playfair_Display'] text-[18px] font-medium text-[#2C1810]">
            Settings
          </span>
          <div className="w-[36px]" />
        </div>
      </header>

      {/* Scrollable content */}
      <main className="flex-1 overflow-y-auto px-5 pb-[env(safe-area-inset-bottom)] pt-2">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="text-[32px] animate-float">💕</div>
          </div>
        ) : (
          <div className="flex flex-col gap-4 pb-8 animate-fade-in-up">
            {/* ── Couple Settings ── */}
            <Section title="Couple">
              <FieldRow
                label="Couple name"
                value={coupleName}
                onChange={isCreator ? setCoupleName : undefined}
                placeholder="e.g. Us Two"
                readOnly={!isCreator}
              />
              <FieldRow
                label="Anniversary date"
                value={anniversaryDate}
                onChange={isCreator ? setAnniversaryDate : undefined}
                type="date"
                readOnly={!isCreator}
              />

              {isCreator && (
                <Row>
                  {saveError && (
                    <p className="font-['DM_Sans'] text-[12px] text-[#C0392B] mb-2">{saveError}</p>
                  )}
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={updateCouple.isPending}
                    className="w-full h-[44px] rounded-[12px] bg-[#D4537E] text-white font-['DM_Sans'] text-[14px] font-medium active:scale-[0.98] transition-all duration-150 disabled:opacity-50 shadow-[0_4px_16px_rgba(212,83,126,0.28)]"
                  >
                    {updateCouple.isPending ? 'Saving…' : saved ? 'Saved!' : 'Save changes'}
                  </button>
                </Row>
              )}

              {!isCreator && (
                <Row>
                  <p className="font-['DM_Sans'] text-[12px] text-[#B89AA2]">
                    Only the couple creator can edit these settings.
                  </p>
                </Row>
              )}
            </Section>

            {/* ── Distance Mode ── */}
            <Section title="Distance mode">
              <ToggleRow
                label="Distance mode"
                description="Changes the app theme to a softer purple palette when you are apart."
                checked={distanceMode}
                onChange={handleDistanceToggle}
              />
            </Section>

            {/* ── Notifications ── */}
            <Section title="Notifications">
              <Row>
                {pushState === 'unsupported' && (
                  <p className="font-['DM_Sans'] text-[13px] text-[#B89AA2]">
                    Push notifications are not supported on this device or browser.
                  </p>
                )}
                {pushState === 'denied' && (
                  <p className="font-['DM_Sans'] text-[13px] text-[#C0392B]">
                    Notifications are blocked. Enable them in your browser or device settings.
                  </p>
                )}
                {pushState === 'subscribed' && (
                  <p className="font-['DM_Sans'] text-[13px] text-[#5B8A5E]">
                    Notifications are enabled on this device ✓
                  </p>
                )}
                {(pushState === 'idle' || pushState === 'requesting') && (
                  <button
                    type="button"
                    onClick={subscribeToPush}
                    disabled={pushState === 'requesting'}
                    className="w-full h-[44px] rounded-[12px] border border-[#D4537E] text-[#D4537E] font-['DM_Sans'] text-[14px] font-medium active:scale-[0.98] active:bg-[#FBEAF0] transition-all duration-150 disabled:opacity-50"
                  >
                    {pushState === 'requesting' ? 'Requesting…' : 'Enable notifications'}
                  </button>
                )}
                {pushState === 'subscribed' && (
                  <div className="mt-3 flex flex-col gap-2">
                    {testState === 'idle' && (
                      <button
                        type="button"
                        onClick={sendTestNotification}
                        className="w-full h-[44px] rounded-[12px] border border-[#D4537E] text-[#D4537E] font-['DM_Sans'] text-[14px] font-medium active:scale-[0.98] active:bg-[#FBEAF0] transition-all duration-150"
                      >
                        Send test notification
                      </button>
                    )}
                    {testState === 'sending' && (
                      <p className="font-['DM_Sans'] text-[13px] text-[#B89AA2] text-center py-2">
                        Sending…
                      </p>
                    )}
                    {testState === 'sent' && (
                      <p className="font-['DM_Sans'] text-[13px] text-[#5B8A5E] text-center py-2">
                        Test sent! Check your notification 💕
                      </p>
                    )}
                    {testState === 'error' && (
                      <p className="font-['DM_Sans'] text-[13px] text-[#C0392B] text-center py-2">
                        Failed to send. Try again.
                      </p>
                    )}
                  </div>
                )}
              </Row>
            </Section>

            {/* ── Invite Code ── */}
            <Section title="Invite code">
              <Row>
                <div className="flex items-center gap-3">
                  <span className="text-code flex-1 select-all">
                    {couple?.invite_code ?? '------'}
                  </span>
                  <button
                    type="button"
                    onClick={handleCopyInvite}
                    className="h-[36px] px-4 rounded-[10px] border border-[#F4C0D1] font-['DM_Sans'] text-[13px] text-[#D4537E] active:scale-[0.96] active:bg-[#FBEAF0] transition-all duration-150 touch-manipulation"
                  >
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <p className="font-['DM_Sans'] text-[12px] text-[#B89AA2] mt-2">
                  Share this code with your partner to connect.
                </p>
              </Row>
            </Section>

            {/* ── Sign out ── */}
            <button
              type="button"
              onClick={handleSignOut}
              className="w-full h-[48px] rounded-[14px] border border-[#F4C0D1] bg-white font-['DM_Sans'] text-[14px] font-medium text-[#C0392B] active:scale-[0.98] active:bg-[#FFF5F5] transition-all duration-150"
            >
              Sign out
            </button>

            {/* ── App version ── */}
            <p className="font-['DM_Sans'] text-[11px] text-[#B89AA2] text-center mt-4">
              Just Us v{APP_VERSION}
            </p>
          </div>
        )}
      </main>
    </div>
  )
}
